import { z } from "zod";
import type { Operation } from "../types";

/**
 * The "Play" next-best-action wire contract. The Play button (top nav) reads
 * `queue` to drive the user through ranked actions and posts `feedback` on
 * every shown / started / completed / skipped / dismissed event — the signal
 * that makes ranking recursively self-improving. Both surfaces are live SWR
 * consumers of the `play` topic so a dismiss in one tab (or an action injected
 * by the out-of-process MCP server) re-ranks everywhere.
 */

export const PLAY_KINDS = [
  "onboarding",
  "fix",
  "integration",
  "automation",
  "report",
  "next-action",
  "agent",
  "info",
] as const;
export type PlayKind = (typeof PLAY_KINDS)[number];

export const PLAY_SIGNALS = [
  "shown",
  "started",
  "completed",
  "skipped",
  "dismissed",
] as const;
export type PlaySignal = (typeof PLAY_SIGNALS)[number];

/**
 * Inline-answer affordance. When present the card is a question whose answer
 * lands in a section brief field — the driver renders an input + Save instead of
 * a "Set this up" CTA. Posted back via the `answer` operation.
 */
/** One inline-answerable brief field. */
export const playAnswerField = z.object({
  fieldKey: z.string(),
  /** Field label — the card headline when walking a multi-field section. */
  label: z.string().optional(),
  /** Field prompt — the card question when walking a multi-field section. */
  prompt: z.string().optional(),
  /**
   * Field subhead — the one-line explanation/examples shown under the question.
   * → docs/questions/PRD.md.
   */
  subhead: z.string().optional(),
  placeholder: z.string().optional(),
  /**
   * The brief field type, so the driver can render the right control inline
   * (a team-member picker for `urn-person`/`urn-people`, a select of `options`
   * when both are present, else free text). Optional for back-compat.
   */
  type: z.string().optional(),
  /**
   * Selectable options for a `type: 'select'` field (INTAKE-PRD §2.8.1 — the
   * intake accept/adjust/reject + question choices). When present the driver
   * renders AnswerOptions (aria-pressed rows, keyboard 1–9) instead of free text;
   * the chosen option's `id` becomes the answer value. Additive/optional so older
   * queue payloads with no options still parse (free-text fields omit it).
   */
  options: z
    .array(z.object({ id: z.string(), title: z.string(), description: z.string().optional() }))
    .optional(),
  /**
   * Allow a free-text answer alongside the options (the intake `__new__`
   * "New object type…" path, where the typed value doubles as the new type name).
   * Additive/optional.
   */
  allowOther: z.boolean().optional(),
});
export type PlayAnswerFieldDTO = z.infer<typeof playAnswerField>;

/**
 * Where an answered value (or a persisted skip) is written. The driver renders
 * the same inline UX for every target; only the route's persistence differs.
 */
export const playAnswerTarget = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("section-brief"), sectionSlug: z.string() }),
  z.object({ kind: z.literal("project-brief"), projectSlug: z.string() }),
  z.object({ kind: z.literal("project-question"), setId: z.number().int() }),
  z.object({
    kind: z.literal("section-question"),
    sectionSlug: z.string(),
    setId: z.number().int(),
  }),
  // Universal Intake decision (INTAKE-PRD §2.8.1): a held batch's accept/adjust/
  // reject (medium) or classify answer (low). The answer route dispatches on this
  // to `confirmIntake` — the option id encodes the decision (`accept:<kind>` /
  // `__raw__` / `__new__`). Additive member — older payloads never carry it.
  z.object({ kind: z.literal("intake"), batchUid: z.string() }),
  // Dynamic Pulse anchor accept/reject (DYNAMIC-PULSE-INTEGRATION-PRD §4 P5): a
  // drift/Tier-2 anchor proposal's confirm card. The answer route dispatches on
  // this to `acceptAnchorProposal`/`rejectAnchorProposal` — the option id
  // encodes the decision (`accept:<seriesId>` / `reject:<seriesId>`). Additive.
  z.object({ kind: z.literal("pulse-anchor"), section: z.string() }),
]);
export type PlayAnswerTargetDTO = z.infer<typeof playAnswerTarget>;

export const playAnswer = playAnswerField.extend({
  target: playAnswerTarget,
  /**
   * Every open field for this answer (highest-priority first). Lets the driver
   * walk the WHOLE thing inline — one prompt card per field — instead of
   * answering one and bouncing the user away for the rest. The top-level
   * `fieldKey`/`placeholder`/`type` mirror `fields[0]`.
   */
  fields: z.array(playAnswerField).optional(),
});
export type PlayAnswerDTO = z.infer<typeof playAnswer>;

/**
 * A generation trigger — when present, the action's CTA fires an async
 * strategist worker (propose a question set, draft the brief, draft one answer)
 * whose output resurfaces as answerable cards on the next re-rank.
 */
export const playTrigger = z.object({
  kind: z.enum(["propose-questions", "fill-brief", "ask-question"]),
  projectSlug: z.string(),
  questionId: z.number().int().optional(),
});
export type PlayTriggerDTO = z.infer<typeof playTrigger>;

/**
 * The object an action is ABOUT — what the §2.5 Dive-in verb opens (Three-Layer
 * PRD §2.5). Additive/optional (back-compat with older queue payloads): when
 * absent the Dive-in resolver derives the route from the action key/ctaHref
 * (resolution order: target.uid → /o/<uid>; target.deepLink → domain route;
 * ctaHref → legacy). Populating it is the engine's option, not a requirement.
 */
export const playTarget = z.object({
  uid: z.string().optional(),
  kind: z.string().optional(),
  deepLink: z.string().optional(),
  mode: z.string().optional(),
  domain: z.string().optional(),
});
export type PlayTargetDTO = z.infer<typeof playTarget>;

export const playAction = z.object({
  key: z.string(),
  kind: z.enum(PLAY_KINDS),
  title: z.string(),
  body: z.string().optional(),
  ctaLabel: z.string().optional(),
  ctaHref: z.string().optional(),
  payoff: z.string().optional(),
  score: z.number().int(),
  reasons: z.array(z.string()),
  source: z.string(),
  answer: playAnswer.optional(),
  trigger: playTrigger.optional(),
  target: playTarget.optional(),
  /**
   * The event that produced this recommendation (Normal Core §6.5.8). When
   * present, the card shows a "Why?" trigger that walks this event's causal
   * ancestry (`/api/events/chain`) into a CausalTraceView. Optional/additive —
   * only candidates derived from the L2 event log (e.g. commitment-risk intel)
   * carry it; everything else omits it and the affordance doesn't render.
   */
  causationUid: z.string().nullable().optional(),
  /**
   * Aggregate kind of the source event ("commitment"), passed to the chain query
   * so the "Why?" trace folds in that aggregate's lifecycle history for depth.
   */
  causationKind: z.string().nullable().optional(),
});
export type PlayActionDTO = z.infer<typeof playAction>;

const queueResponse = z.object({
  actions: z.array(playAction),
  total: z.number().int(),
});
export type PlayQueueResponse = z.infer<typeof queueResponse>;

const queueOp: Operation<void, void, void, PlayQueueResponse> = {
  method: "GET",
  path: () => "/api/play/queue",
  response: queueResponse,
};

const feedbackBody = z.object({
  actionKey: z.string().min(1),
  kind: z.enum(PLAY_KINDS),
  signal: z.enum(PLAY_SIGNALS),
  reason: z.string().max(500).optional(),
  /**
   * The card's source event uid (when it has one), so a DISMISS override is
   * genuinely chained to the recommendation in the causal graph (§6.5.8) — a
   * later "Why?" trace can then show the human-in-the-loop step. Optional: only
   * event-derived cards carry it.
   */
  causationUid: z.string().nullable().optional(),
});
export type PlayFeedbackBody = z.infer<typeof feedbackBody>;

const feedbackResponse = z.object({ ok: z.boolean() });

const feedbackOp: Operation<
  void,
  void,
  z.infer<typeof feedbackBody>,
  z.infer<typeof feedbackResponse>
> = {
  method: "POST",
  path: () => "/api/play/feedback",
  body: feedbackBody,
  response: feedbackResponse,
};

const answerBody = z.object({
  actionKey: z.string().min(1),
  target: playAnswerTarget,
  fieldKey: z.string().min(1),
  /** The answer text. Omitted (with `skip:true`) when skipping a question. */
  value: z.string().max(2000).optional(),
  /** Persist a skip (question targets only — writes skip_reason, advances the set). */
  skip: z.boolean().optional(),
  /** Optional skip reason. */
  reason: z.string().max(500).optional(),
});
export type PlayAnswerBody = z.infer<typeof answerBody>;

const answerResponse = z.object({ ok: z.boolean() });

const answerOp: Operation<
  void,
  void,
  z.infer<typeof answerBody>,
  z.infer<typeof answerResponse>
> = {
  method: "POST",
  path: () => "/api/play/answer",
  body: answerBody,
  response: answerResponse,
};

const triggerBody = z.object({
  kind: z.enum(["propose-questions", "fill-brief", "ask-question"]),
  projectSlug: z.string().min(1),
  questionId: z.number().int().optional(),
});
export type PlayTriggerBody = z.infer<typeof triggerBody>;

const triggerResponse = z.object({ ok: z.boolean(), reason: z.string().optional() });

const triggerOp: Operation<
  void,
  void,
  z.infer<typeof triggerBody>,
  z.infer<typeof triggerResponse>
> = {
  method: "POST",
  path: () => "/api/play/trigger",
  body: triggerBody,
  response: triggerResponse,
};

// ── play.propose — inject/refresh a durable play_actions card ────────────────
//
// The thin client-reachable path onto the SAME writer the play-strategist
// agent / intake / audit-reconcile use (`upsertPlayAction`,
// `@excellent/core/server`) — a card proposed from a UI surface (e.g. the
// Dynamic Pulse insight modal's "Propose a fix", §4 P4) is indistinguishable
// from one an agent injected. Idempotent: upserts on `(workspace_id,
// actionKey)` and never reopens a card the user dismissed.

const proposeBody = z.object({
  actionKey: z.string().min(1),
  kind: z.enum(PLAY_KINDS),
  title: z.string().min(1),
  body: z.string().max(2000).optional(),
  ctaLabel: z.string().max(80).optional(),
  ctaHref: z.string().optional(),
  importance: z.number().int().min(0).max(100).optional(),
});
export type PlayProposeBody = z.infer<typeof proposeBody>;

const proposeResponse = z.object({
  ok: z.boolean(),
  /** True only when this call inserted a brand-new row (§4 P4's "refresh" case
   *  — the card already existed — returns `created: false`, still `ok: true`). */
  created: z.boolean(),
});
export type PlayProposeResponse = z.infer<typeof proposeResponse>;

const proposeOp: Operation<
  void,
  void,
  z.infer<typeof proposeBody>,
  z.infer<typeof proposeResponse>
> = {
  method: "POST",
  path: () => "/api/play/propose",
  body: proposeBody,
  response: proposeResponse,
};

export const play = {
  queue: queueOp,
  feedback: feedbackOp,
  answer: answerOp,
  trigger: triggerOp,
  propose: proposeOp,
};
